import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  MapPinIcon,
  DocumentArrowUpIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import ConfirmDialog from '../components/ConfirmDialog.tsx'

interface City {
  id: string
  name: string
  state: string
  latitude: number
  longitude: number
}

export default function Cities() {
  const queryClient = useQueryClient()
  const [selectedState, setSelectedState] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showImportModal, setShowImportModal] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean
    cityId: string | null
    cityName: string
  }>({
    isOpen: false,
    cityId: null,
    cityName: '',
  })

  // States list
  const states = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ]

  // Fetch cities
  const { data: cities, isLoading } = useQuery(
    ['cities', selectedState, searchTerm],
    () => {
      const params = new URLSearchParams()
      if (selectedState) params.append('state', selectedState)
      if (searchTerm) params.append('search', searchTerm)
      return api.get(`/api/cities/?${params.toString()}`)
    },
    {
      keepPreviousData: true,
    }
  )

  // Fetch total cities count
  const { data: cityStats } = useQuery(
    'city-stats',
    () => api.get('/api/cities/stats'),
    {
      keepPreviousData: true,
    }
  )

  // Delete city mutation
  const deleteCityMutation = useMutation(
    (cityId: string) => api.delete(`/api/cities/${cityId}`),
    {
      onSuccess: () => {
        toast.success('City deleted successfully')
        queryClient.invalidateQueries('cities')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to delete city')
      },
    }
  )

  const handleDeleteCity = (cityId: string, cityName: string) => {
    setDeleteDialog({
      isOpen: true,
      cityId,
      cityName,
    })
  }

  const confirmDeleteCity = () => {
    if (deleteDialog.cityId) {
      deleteCityMutation.mutate(deleteDialog.cityId)
      setDeleteDialog({
        isOpen: false,
        cityId: null,
        cityName: '',
      })
    }
  }

  const cancelDeleteCity = () => {
    setDeleteDialog({
      isOpen: false,
      cityId: null,
      cityName: '',
    })
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='sm:flex sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
            Cities
          </h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Manage cities data for scraping jobs
          </p>
          {cityStats?.data && (
            <div className='mt-2 flex items-center space-x-4'>
              <div className='flex items-center space-x-1'>
                <MapPinIcon className='h-4 w-4 text-blue-500' />
                <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Total Cities:{' '}
                  {cityStats.data.total_cities?.toLocaleString() || '0'}
                </span>
              </div>
              <div className='flex items-center space-x-1'>
                <span className='text-sm text-gray-500 dark:text-gray-400'>
                  States: {cityStats.data.states_with_cities || '0'}
                </span>
              </div>
              {selectedState && cityStats.data.state_counts && (
                <div className='flex items-center space-x-1'>
                  <span className='text-sm font-medium text-green-700 dark:text-green-400'>
                    {selectedState}:{' '}
                    {cityStats.data.state_counts[
                      selectedState
                    ]?.toLocaleString() || '0'}{' '}
                    cities
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className='mt-4 sm:mt-0'>
          <button
            onClick={() => setShowImportModal(true)}
            className='btn-primary'
          >
            <PlusIcon className='h-4 w-4 mr-2' />
            Add State
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className='bg-white shadow rounded-lg p-4 dark:bg-gray-800'>
        <div className='flex items-center space-x-4'>
          <div>
            <label
              htmlFor='state-filter'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              State
            </label>
            <select
              id='state-filter'
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className='select mt-1'
            >
              <option value=''>All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className='flex-1'>
            <label
              htmlFor='search'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Search Cities
            </label>
            <input
              type='text'
              id='search'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Search by city name...'
              className='input mt-1'
            />
          </div>
        </div>
      </div>

      {/* Cities Table */}
      <div className='bg-white shadow overflow-hidden sm:rounded-md dark:bg-gray-800'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
            <thead className='bg-gray-50 dark:bg-gray-700'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                  City Name
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                  State
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                  Latitude
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                  Longitude
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
              {cities?.data?.map((city: City) => (
                <tr
                  key={city.id}
                  className='hover:bg-gray-50 dark:hover:bg-gray-700'
                >
                  <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white'>
                    {city.name}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                    {city.state}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                    {city.latitude.toFixed(6)}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                    {city.longitude.toFixed(6)}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                    <button
                      onClick={() => handleDeleteCity(city.id, city.name)}
                      className='text-red-600 hover:text-red-500'
                      title='Delete city'
                    >
                      <TrashIcon className='h-4 w-4' />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {cities?.data?.length === 0 && (
          <div className='text-center py-12'>
            <MapPinIcon className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
              No cities found
            </h3>
            <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
              {selectedState
                ? `No cities found for state ${selectedState}`
                : 'No cities in database. Import cities for a state to get started.'}
            </p>
            <div className='mt-6'>
              <button
                onClick={() => setShowImportModal(true)}
                className='btn-primary'
              >
                <DocumentArrowUpIcon className='h-4 w-4 mr-2' />
                Import Cities
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <ImportCitiesModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false)
            queryClient.invalidateQueries('cities')
            queryClient.invalidateQueries('city-stats')
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteCity}
        onConfirm={confirmDeleteCity}
        title='Delete City'
        message={`Are you sure you want to delete "${deleteDialog.cityName}"? This action cannot be undone.`}
        confirmText='Delete'
        confirmButtonClass='bg-red-600 hover:bg-red-700 focus:ring-red-500'
      />
    </div>
  )
}

// Import Cities Modal Component
interface ImportCitiesModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function ImportCitiesModal({
  isOpen,
  onClose,
  onSuccess,
}: ImportCitiesModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedState, setSelectedState] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  const states = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ]

  const importCitiesMutation = useMutation(
    async (formData: FormData) => {
      const response = await api.post('/api/cities/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success(
          `Successfully imported ${data.imported_count} cities for ${selectedState}`
        )
        onSuccess()
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to import cities')
        setIsUploading(false)
      },
    }
  )

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleSubmit = async () => {
    if (!selectedFile || !selectedState) {
      toast.error('Please select a file and state')
      return
    }

    setIsUploading(true)
    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('state', selectedState)

    importCitiesMutation.mutate(formData)
  }

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex min-h-screen items-center justify-center p-4'>
        <div
          className='fixed inset-0 bg-black bg-opacity-50 transition-opacity'
          onClick={onClose}
        />

        <div className='relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full'>
          {/* Header */}
          <div className='flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
              Import Cities
            </h3>
            <button
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className='p-6 space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              >
                <option value=''>Select a state</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                CSV/Excel File
              </label>
              <input
                type='file'
                accept='.csv,.xlsx,.xls'
                onChange={handleFileChange}
                className='block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-700 dark:file:text-gray-300'
              />
              <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                Expected format: City Name, Latitude, Longitude
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className='flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700'>
            <button
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600'
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedFile || !selectedState || isUploading}
              className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isUploading ? (
                <>
                  <LoadingSpinner />
                  <span className='ml-2'>Importing...</span>
                </>
              ) : (
                'Import Cities'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
